import * as React from 'react'
import { useState } from 'react'
import dayjs from 'dayjs'
import moment from 'moment'
import 'dayjs/locale/pl'
import 'dayjs/locale/en'
import { useQueryDashboard } from './dashboard/useQueryDashboard'
import { useQueryAlertPreview } from '@/modules/timetable/hooks/useQueryAlertPreview'
import { useNavigate, useParams } from 'react-router-dom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FaIcon, faBellSolid } from '@/lib/fa'
import { TIMETABLE_ROUTES } from '@/modules/timetable/routes-typed'
import { AvatarCard } from './dashboard/components/AvatarCard'
import { MonthNavigator } from './dashboard/components/MonthNavigator'
import { PersonnelAndFinanceCard } from './dashboard/components/PersonnelAndFinanceCard'
import { DriversHoursCard } from './dashboard/components/DriversHoursCard'
import { VehicleUsageCard } from './dashboard/components/VehicleUsageCard'
import { ViolationsCard } from './dashboard/components/ViolationsCard'
import { FavouriteReportsCard } from './dashboard/components/FavouriteReportsCard'
import { CurrentDetailedDate } from '@/app/features/common/CurrentDetailedDate'
import { CompanyNameBadge } from '@/modules/drivers/shared/viewBars/CompanyNameBadge'
import { ImportPopupContainer } from '@/modules/imports/container/import-popup-container'
import { TaskStatusWrapper } from '@/modules/shared/components'
import { useDateStore } from '@/modules/drivers/stores/dateStore'

export type Step = {
  id?: string | number
  title: string
  status?: 'completed' | 'active' | 'upcoming'
}

/** Rows rendered in the alert bell; the rest are collapsed into the "+N" counter. */
const ALERTS_SHOWN = 8

export const DashboardHomePage = () => {
  const { activeLanguage, t } = useLocalize()
  const lang = activeLanguage?.code || 'pl'
  const navigate = useNavigate()

  const getCurrentMonth = () => {
    return dayjs().locale(lang)
  }

  const getInitialMonth = () => {
    const persistedMonth = useDateStore.getState().month
    if (persistedMonth) {
      const parsed = dayjs(persistedMonth, 'YYYY-MM').locale(lang)
      if (parsed.isValid()) return parsed
    }
    return getCurrentMonth().subtract(1, 'month')
  }

  const [selectedMonth, setSelectedMonth] = useState(getInitialMonth)
  const { companyId } = useParams<{ companyId: string }>()
  const { data, error, isFetching } = useQueryDashboard(
    companyId,
    selectedMonth,
  )

  const setDateStoreMonth = useDateStore((s) => s.setMonth)
  React.useEffect(() => {
    setDateStoreMonth(selectedMonth.format('YYYY-MM'))
  }, [selectedMonth, setDateStoreMonth])

  // The bell shows ALERTS_SHOWN rows and a "+N" counter, so it asks for exactly that:
  // the earliest few plus the total. It used to fetch the whole [-1 month, +1 month]
  // window — 241 alerts on a large company — only to read `.length` for the counter.
  // The window and the ordering now live server-side in AlertPreviewRequestHandler.
  const { data: alertPreview, isLoading: isLoadingAlerts } =
    useQueryAlertPreview(companyId ?? '', ALERTS_SHOWN)

  const alerts = alertPreview?.items ?? []
  const alertsTotal = alertPreview?.total ?? 0

  const [openImport, setOpenImport] = useState<boolean>(false)
  const importData = () => setOpenImport(true)
  const closeImport = () => setOpenImport(false)

  const goToTimetable = () => {
    if (!companyId) return
    navigate(TIMETABLE_ROUTES.alertsList.buildPath({ companyId }))
  }

  const settlementMonth = `${selectedMonth.format('MMMM')} ${selectedMonth.format('YYYY')}`

  const goToPrevMonth = () => {
    setSelectedMonth((prev) => prev.subtract(1, 'month'))
  }

  const goToNextMonth = () => {
    const maxMonth = getCurrentMonth()
    setSelectedMonth((prev) =>
      prev.isBefore(maxMonth, 'month') ? prev.add(1, 'month') : prev,
    )
  }

  const isLoadingData = !data
  const isRefreshingData = !!data && isFetching

  if (!data && error) {
    // Errors that GlobalErrorHandler already explains in a modal (company still migrating,
    // service company with no database) must not also dump a raw AxiosError behind it.
    const handledGlobally = ['NOT_MIGRATED_COMPANY', 'COMPANY_HAS_NO_DATABASE'].includes(
      (error as any)?.response?.data?.ErrorCode,
    )
    return (
      <TaskStatusWrapper>
        {!handledGlobally && <p>Error: {String(error)}</p>}
      </TaskStatusWrapper>
    )
  }

  const hasAlerts = alertsTotal > 0
  const bellAriaLabel = `${t('nav.calendar')}${hasAlerts ? ` (${alertsTotal})` : ''}`

  return (
    <TaskStatusWrapper>
      <ImportPopupContainer
        isOpen={openImport}
        handleClose={closeImport}
        companyId={companyId}
        onSuccess={closeImport}
      />
      <div className="tw-flex tw-h-full tw-flex-1 tw-flex-col tw-overflow-y-auto tw-rounded-lg tw-bg-base-100 tw-pb-0 xl:tw-overflow-hidden">
        <header className="tw-flex tw-flex-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-px-4 tw-py-3">
          <CompanyNameBadge />
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-group tw-relative tw-flex">
              <button
                type="button"
                onClick={goToTimetable}
                aria-label={bellAriaLabel}
                className="tw-relative tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-primary tw-transition-colors hover:tw-bg-base-200"
              >
                <FaIcon icon={faBellSolid} />
                {hasAlerts && (
                  <span
                    className="tw-absolute -tw-right-0.5 -tw-top-0.5 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-red-500"
                    aria-hidden="true"
                  />
                )}
              </button>
              <div
                role="tooltip"
                className="tw-pointer-events-none tw-absolute tw-right-0 tw-top-full tw-z-50 tw-mt-2 tw-w-72 tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-black tw-opacity-0 tw-shadow-lg tw-transition-opacity tw-duration-150 group-hover:tw-opacity-100"
              >
                <div className="tw-mb-1 tw-font-semibold">
                  {t('nav.calendar')}
                </div>
                {isLoadingAlerts ? (
                  <p className="tw-opacity-70">{t('gridPager.loadingText')}</p>
                ) : !hasAlerts ? (
                  <p className="tw-opacity-70">{t('alerts.empty-results')}</p>
                ) : (
                  <ul className="tw-flex tw-max-h-64 tw-flex-col tw-gap-1 tw-overflow-y-auto">
                    {alerts.map((alert, index) => (
                      <li
                        // Nie sam alertId: czesc alertow przychodzi z alertId = 0,
                        // wiec klucze sie powielaly i React mogl gubic albo dublowac
                        // wiersze na tej liscie.
                        key={`${alert.alertId}-${index}`}
                        className="tw-flex tw-items-baseline tw-justify-between tw-gap-2 tw-border-b tw-border-base-200 tw-pb-1 last:tw-border-0"
                      >
                        <span className="tw-truncate">
                          {alert.object !== '-' ? `${alert.object} — ` : ''}
                          {alert.text}
                        </span>
                        <span className="tw-flex-shrink-0 tw-opacity-60">
                          {moment(alert.eventTime).format('DD.MM.YYYY')}
                        </span>
                      </li>
                    ))}
                    {alertsTotal > alerts.length && (
                      <li className="tw-pt-1 tw-text-center tw-opacity-70">
                        +{alertsTotal - alerts.length}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
            <CurrentDetailedDate className="tw-h-8 !tw-text-base" />
          </div>
        </header>

        <div className="tw-flex tw-w-full tw-flex-col xl:tw-h-full xl:tw-min-h-[800px] xl:tw-flex-row xl:tw-items-stretch">
          <div className="tw-w-full xl:tw-w-[286px] xl:tw-flex-shrink-0 xl:tw-self-stretch">
            <div className="tw-h-full">
              <AvatarCard
                stats={data?.imports}
                importData={importData}
                selectedMonth={selectedMonth}
                isLoading={isLoadingData}
                isRefreshing={isRefreshingData}
              />
            </div>
          </div>

          <div className="tw-flex-1 xl:tw-self-stretch">
            <div className="tw-h-full">
              <div className="tw-flex tw-flex-1 tw-flex-col md:tw-flex-1 xl:tw-overflow-hidden">
                <MonthNavigator
                  goToNextMonth={goToNextMonth}
                  goToPrevMonth={goToPrevMonth}
                  settlementMonth={settlementMonth}
                  selectedMonth={selectedMonth}
                  maxMonth={getCurrentMonth()}
                />
                <div className="tw-flex tw-flex-shrink-0 tw-flex-col tw-border-b tw-border-base-300 lg:tw-flex-row">
                  <PersonnelAndFinanceCard
                    activeDrivers={data?.drivers?.activeCount}
                    activeVehicles={data?.vehicles?.activeCount}
                    stats={data?.payroll}
                    isLoading={isLoadingData}
                    isRefreshing={isRefreshingData}
                  />
                  <div className="tw-flex tw-flex-col sm:tw-flex-row lg:tw-w-2/3">
                    <DriversHoursCard
                      stats={data?.driverWorkTime}
                      isLoading={isLoadingData}
                      isRefreshing={isRefreshingData}
                      className="tw-w-full !tw-rounded-none tw-border-b-0 tw-border-l-0 tw-border-r tw-border-t-0 tw-pb-0 sm:tw-border-r-0"
                    />
                    <VehicleUsageCard
                      vehicleUsage={data?.vehicleUsage}
                      isLoading={isLoadingData}
                      isRefreshing={isRefreshingData}
                    />
                  </div>
                </div>
                <div className="tw-flex tw-w-full tw-flex-1 tw-flex-col xl:tw-flex-row xl:tw-overflow-hidden">
                  <ViolationsCard
                    date={settlementMonth}
                    violations={data?.violations}
                    selectedMonth={selectedMonth}
                    isLoading={isLoadingData}
                    isRefreshing={isRefreshingData}
                    className="tw-flex tw-flex-1 tw-flex-col !tw-rounded-none tw-border-l-0 tw-border-t-0 md:tw-min-w-96 xl:tw-overflow-hidden xl:tw-border-b-0"
                  />
                  <FavouriteReportsCard className="tw-flex tw-flex-col !tw-rounded-none tw-border-b-0 tw-border-l-0 tw-border-t-0 xl:tw-overflow-hidden xl:tw-border-r-0 2xl:tw-flex-1" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TaskStatusWrapper>
  )
}
